import type {
  AgeingBucket,
  BookingType,
  ContributionStats,
  DashboardMetrics,
  InsightPeriodMode,
  MonthlyData,
  MonthlyDataByKey,
  MonthlyVehicleStats,
  PartyOutstanding,
  RiskCode,
  Trip,
  TripRisk,
  TripStatus,
  VehicleSource,
} from "./types";
import { calculateFinancials, daysSince, getTripRisks } from "./rules";

export type DashboardPeriod = "today" | "week";
export type RisksByTrip = Map<string, TripRisk[]>;
export type SortableTripField = "date" | "profit" | "outstanding";
export type SortDirection = "asc" | "desc";
export type TripSortState = {
  field: SortableTripField;
  direction: SortDirection;
};
export type TripFieldSuggestions = {
  partyName: string[];
  brokerName: string[];
  vehicleNo: string[];
  driverName: string[];
  origin: string[];
  destination: string[];
};
export type OperationalQueues = {
  deliveredNotBilled: Trip[];
  overdueReceivables: Trip[];
};
export type MetricDelta = {
  value: number;
  pct: number;
};
export type InsightDeltas = {
  totalHire: MetricDelta;
  totalExp: MetricDelta;
  totalProfit: MetricDelta;
  totalOutstanding: MetricDelta;
};
export type InsightQualitySignals = {
  repeatPartyRate: number;
  lowMarginShare: number;
  averageDaysToSettle: number;
};
export type RouteCostVariance = {
  route: string;
  trips: number;
  periodAvgExpense: number;
  historicalAvgExpense: number;
  variancePct: number;
};
export type DashboardTrendPoint = {
  monthKey: string;
  monthLabel: string;
  active: number;
  notBilled: number;
  overdue: number;
  exceptions: number;
};
export type SourceSummary = {
  topBrokerName: string;
  topBrokerProfit: number;
  marketVehicleProfit: number;
};

export const INSIGHT_MONTHS = [
  { value: "01", label: "January" },
  { value: "02", label: "February" },
  { value: "03", label: "March" },
  { value: "04", label: "April" },
  { value: "05", label: "May" },
  { value: "06", label: "June" },
  { value: "07", label: "July" },
  { value: "08", label: "August" },
  { value: "09", label: "September" },
  { value: "10", label: "October" },
  { value: "11", label: "November" },
  { value: "12", label: "December" },
] as const;

export const QUICK_TRIP_FILTERS = [
  "all",
  "active",
  "pendingPayment",
  "deliveredNotBilled",
  "lowMargin",
  "brokerTrips",
  "ownVehicles",
  "marketVehicles",
] as const;

export type QuickTripFilter = (typeof QUICK_TRIP_FILTERS)[number];

export type TripFilters = {
  quickFilter: QuickTripFilter;
  status: "all" | TripStatus;
  bookingType: "all" | BookingType;
  vehicleSource: "all" | VehicleSource;
  dateFrom: string;
  dateTo: string;
};

export const DEFAULT_TRIP_FILTERS: TripFilters = {
  quickFilter: "all",
  status: "all",
  bookingType: "all",
  vehicleSource: "all",
  dateFrom: "",
  dateTo: "",
};

export type CashSort = "oldest" | "largest" | "party";
export type CashFilters = {
  query: string;
  ageingBucket: "all" | AgeingBucket;
  party: "all" | string;
  status: "all" | TripStatus;
  sort: CashSort;
};

export const DEFAULT_CASH_FILTERS: CashFilters = {
  query: "",
  ageingBucket: "all",
  party: "all",
  status: "all",
  sort: "oldest",
};

export type PartySort = "outstanding" | "oldest" | "trips" | "name";
export type PartyFilters = {
  query: string;
  ageingBucket: "all" | AgeingBucket;
  sort: PartySort;
};

export const DEFAULT_PARTY_FILTERS: PartyFilters = {
  query: "",
  ageingBucket: "all",
  sort: "outstanding",
};

const DATA_QUALITY_CODES: RiskCode[] = [
  "missingVehicle",
  "missingParty",
  "missingBroker",
  "missingHire",
  "missingExpense",
];

const emptyMonthlyData = (): MonthlyData => ({
  totalHire: 0,
  totalExp: 0,
  totalProfit: 0,
  totalOutstanding: 0,
  vehicles: {},
  parties: {},
  routes: {},
  brokers: {},
});

const emptyVehicleStats = (): MonthlyVehicleStats => ({
  trips: 0,
  hire: 0,
  expense: 0,
  profit: 0,
  outstanding: 0,
  marginPct: 0,
});

const addContribution = (
  bucket: Record<string, ContributionStats>,
  key: string,
  trip: Trip
) => {
  const safeKey = key || "Unassigned";
  if (!bucket[safeKey]) bucket[safeKey] = emptyVehicleStats();

  const financials = calculateFinancials(trip);
  const stats = bucket[safeKey];
  stats.trips += 1;
  stats.hire += trip.hireAmount;
  stats.expense += financials.totalExpense;
  stats.profit += financials.operatingProfit;
  stats.outstanding += financials.outstanding;
  stats.marginPct = stats.hire > 0 ? stats.profit / stats.hire : 0;
};

const isReportableTrip = (trip: Trip): boolean =>
  trip.status !== "Cancelled" && trip.status !== "Draft";

const routeLabel = (trip: Trip): string =>
  [trip.origin, trip.destination].filter(Boolean).join(" - ") || "Unassigned";

export const filterTrips = (
  trips: Trip[],
  query: string,
  filters: TripFilters = DEFAULT_TRIP_FILTERS
): Trip[] => {
  const normalized = query.trim().toLowerCase();

  return trips.filter((trip) => {
    const financials = calculateFinancials(trip);
    const searchable = trip.searchIndex || "";

    if (normalized && !searchable.includes(normalized)) return false;
    if (filters.status !== "all" && trip.status !== filters.status) return false;
    if (filters.bookingType !== "all" && trip.bookingType !== filters.bookingType) {
      return false;
    }
    if (
      filters.vehicleSource !== "all" &&
      trip.vehicleSource !== filters.vehicleSource
    ) {
      return false;
    }
    if (filters.dateFrom && trip.date < filters.dateFrom) return false;
    if (filters.dateTo && trip.date > filters.dateTo) return false;

    switch (filters.quickFilter) {
      case "active":
        return ["Dispatched", "In Transit"].includes(trip.status);
      case "pendingPayment":
        return financials.outstanding > 0 && trip.status !== "Cancelled";
      case "deliveredNotBilled":
        return trip.status === "Delivered" && financials.outstanding > 0;
      case "lowMargin":
        return trip.hireAmount > 0 && financials.marginPct < 0.12;
      case "brokerTrips":
        return ["Broker", "Indirect"].includes(trip.bookingType);
      case "ownVehicles":
        return trip.vehicleSource === "Own Vehicle";
      case "marketVehicles":
        return trip.vehicleSource === "Market Vehicle";
      default:
        return true;
    }
  });
};

export const getRisksByTrip = (trips: Trip[], today = new Date()): RisksByTrip =>
  new Map(trips.map((trip) => [trip.id, getTripRisks(trip, today)]));

export const getDashboardMetrics = (
  trips: Trip[],
  risksByTrip: RisksByTrip = getRisksByTrip(trips)
): DashboardMetrics =>
  trips.reduce<DashboardMetrics>(
    (acc, trip) => {
      if (trip.status === "Cancelled" || trip.status === "Draft") return acc;

      const financials = calculateFinancials(trip);
      acc.totalHire += trip.hireAmount;
      acc.totalExpense += financials.totalExpense;
      acc.totalProfit += financials.operatingProfit;
      acc.totalOutstanding += financials.outstanding;
      if (["Dispatched", "In Transit"].includes(trip.status)) acc.activeTrips += 1;
      if (financials.marginPct < 0.12) acc.lowMarginTrips += 1;
      if (["Broker", "Indirect"].includes(trip.bookingType)) acc.brokerTrips += 1;
      if (trip.vehicleSource === "Market Vehicle") acc.marketVehicleTrips += 1;
      const risks = risksByTrip.get(trip.id) ?? [];
      acc.dataQualityIssues += risks.filter((risk) =>
        DATA_QUALITY_CODES.includes(risk.code)
      ).length;
      if (trip.status === "Delivered" && financials.outstanding > 0) {
        acc.deliveredNotBilled += 1;
      }
      if (
        ["Delivered", "Billed", "Partially Settled"].includes(trip.status) &&
        financials.outstanding > 0
      ) {
        acc.overdueTrips += risks.some((risk) => risk.code === "overdue") ? 1 : 0;
      }
      return acc;
    },
    {
      totalHire: 0,
      totalExpense: 0,
      totalProfit: 0,
      totalOutstanding: 0,
      activeTrips: 0,
      lowMarginTrips: 0,
      overdueTrips: 0,
      deliveredNotBilled: 0,
      brokerTrips: 0,
      marketVehicleTrips: 0,
      dataQualityIssues: 0,
    }
  );

export const getActiveTrips = (trips: Trip[]): Trip[] =>
  trips.filter((trip) => ["Dispatched", "In Transit"].includes(trip.status));

export const getExceptionQueue = (
  trips: Trip[],
  risksByTrip: RisksByTrip = getRisksByTrip(trips)
): TripRisk[] =>
  trips
    .flatMap((trip) => risksByTrip.get(trip.id) ?? [])
    .sort((a, b) => {
      const severityRank = { critical: 3, warning: 2, info: 1 };
      return (
        severityRank[b.severity] - severityRank[a.severity] ||
        b.moneyImpact - a.moneyImpact
      );
    });

export const getOperationalQueues = (
  trips: Trip[],
  risksByTrip: RisksByTrip = getRisksByTrip(trips)
): OperationalQueues => ({
  deliveredNotBilled: trips.filter(
    (trip) => trip.status === "Delivered" && calculateFinancials(trip).outstanding > 0
  ),
  overdueReceivables: trips.filter((trip) =>
    (risksByTrip.get(trip.id) ?? []).some((risk) => risk.code === "overdue")
  ),
});

const isSameLocalDate = (date: string, today: Date): boolean =>
  date === today.toISOString().slice(0, 10);

const isWithinCurrentWeek = (date: string, today: Date): boolean => {
  const tripDate = new Date(`${date}T00:00:00`);
  if (Number.isNaN(tripDate.getTime())) return false;
  const todayStart = new Date(today);
  todayStart.setHours(0, 0, 0, 0);
  const day = todayStart.getDay();
  const mondayOffset = day === 0 ? -6 : 1 - day;
  const weekStart = new Date(todayStart);
  weekStart.setDate(todayStart.getDate() + mondayOffset);
  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekStart.getDate() + 7);
  return tripDate >= weekStart && tripDate < weekEnd;
};

export const getTripsForDashboardPeriod = (
  trips: Trip[],
  period: DashboardPeriod,
  today = new Date()
): Trip[] =>
  trips.filter((trip) =>
    period === "today"
      ? isSameLocalDate(trip.date, today)
      : isWithinCurrentWeek(trip.date, today)
  );

const shiftMonthKey = (monthKey: string, offset: number): string => {
  const [year, month] = monthKey.split("-").map(Number);
  const date = new Date(Date.UTC(year, month - 1 + offset, 1));
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, "0")}`;
};

const monthShortLabel = (monthKey: string): string =>
  new Date(`${monthKey}-01T00:00:00Z`)
    .toLocaleString("en-IN", { month: "short" })
    .toUpperCase();

export const getDashboardStatusTrend = (
  trips: Trip[],
  risksByTrip: RisksByTrip = getRisksByTrip(trips),
  today = new Date()
): DashboardTrendPoint[] => {
  const latestDataMonth = getAvailableMonths(getMonthlyData(trips))[0];
  const endMonth = latestDataMonth ?? today.toISOString().slice(0, 7);
  const monthKeys = Array.from({ length: 6 }, (_, index) =>
    shiftMonthKey(endMonth, index - 5)
  );

  return monthKeys.map((monthKey) => {
    const monthTrips = trips.filter(
      (trip) =>
        (trip.monthKey || trip.date.slice(0, 7)) === monthKey &&
        isReportableTrip(trip)
    );
    const monthRisks = monthTrips.flatMap(
      (trip) => risksByTrip.get(trip.id) ?? []
    );

    return {
      monthKey,
      monthLabel: monthShortLabel(monthKey),
      active: monthTrips.filter((trip) =>
        ["Dispatched", "In Transit"].includes(trip.status)
      ).length,
      notBilled: monthTrips.filter(
        (trip) =>
          trip.status === "Delivered" &&
          calculateFinancials(trip).outstanding > 0
      ).length,
      overdue: monthRisks.filter((risk) => risk.code === "overdue").length,
      exceptions: monthRisks.length,
    };
  });
};

export const getMonthlyData = (trips: Trip[]): MonthlyDataByKey => {
  const data: MonthlyDataByKey = {};

  trips.forEach((trip) => {
    if (!isReportableTrip(trip)) return;

    const month = trip.monthKey || trip.date.slice(0, 7);
    if (!data[month]) data[month] = emptyMonthlyData();

    const financials = calculateFinancials(trip);
    const monthData = data[month];
    monthData.totalHire += trip.hireAmount;
    monthData.totalExp += financials.totalExpense;
    monthData.totalProfit += financials.operatingProfit;
    monthData.totalOutstanding += financials.outstanding;

    addContribution(monthData.vehicles, trip.vehicleNo, trip);
    addContribution(monthData.parties, trip.partyName, trip);
    addContribution(monthData.routes, routeLabel(trip), trip);
    if (trip.brokerName) addContribution(monthData.brokers, trip.brokerName, trip);
  });

  return data;
};

export const getDistinctTripValues = (
  trips: Trip[],
  field: keyof Pick<
    Trip,
    "partyName" | "brokerName" | "vehicleNo" | "driverName" | "origin" | "destination"
  >
): string[] =>
  Array.from(
    new Set(trips.map((trip) => trip[field]).filter((value) => value.trim()))
  ).sort((a, b) => a.localeCompare(b));

export const getTripFieldSuggestions = (trips: Trip[]): TripFieldSuggestions => ({
  partyName: getDistinctTripValues(trips, "partyName"),
  brokerName: getDistinctTripValues(trips, "brokerName"),
  vehicleNo: getDistinctTripValues(trips, "vehicleNo"),
  driverName: getDistinctTripValues(trips, "driverName"),
  origin: getDistinctTripValues(trips, "origin"),
  destination: getDistinctTripValues(trips, "destination"),
});

export const getAvailableYears = (trips: Trip[]): string[] =>
  Array.from(
    new Set(
      Object.keys(getMonthlyData(trips))
        .map((monthKey) => monthKey.slice(0, 4))
        .filter((year) => /^\d{4}$/.test(year))
    )
  )
    .sort()
    .reverse();

export const getInsightYearOptions = (trips: Trip[]): string[] =>
  getAvailableYears(trips);

export const getInsightMonthOptions = (
  trips: Trip[],
  year: string
): string[] => {
  if (!year) return [];

  const monthsWithData = new Set(
    Object.keys(getMonthlyData(trips))
      .filter((monthKey) => monthKey.startsWith(year))
      .map((monthKey) => monthKey.slice(5, 7))
  );

  return INSIGHT_MONTHS.map((month) => month.value).filter((month) =>
    monthsWithData.has(month)
  );
};

export const getDefaultInsightYear = (trips: Trip[]): string =>
  getAvailableYears(trips)[0] ?? "";

export const getInsightPeriodOptions = (
  trips: Trip[],
  mode: InsightPeriodMode,
  monthlyData: MonthlyDataByKey
): string[] => {
  if (mode === "all") return ["all"];
  if (mode === "year") return getAvailableYears(trips);
  return getAvailableMonths(monthlyData);
};

export const getInsightData = (
  trips: Trip[],
  mode: InsightPeriodMode,
  periodKey: string
): MonthlyData => {
  if (mode === "all") {
    return Object.values(getMonthlyData(trips)).reduce<MonthlyData>(
      (acc, month) => mergeMonthlyData(acc, month),
      emptyMonthlyData()
    );
  }

  if (!periodKey) return emptyMonthlyData();

  if (mode === "year") {
    return Object.entries(getMonthlyData(trips)).reduce<MonthlyData>(
      (acc, [month, data]) =>
        month.startsWith(periodKey) ? mergeMonthlyData(acc, data) : acc,
      emptyMonthlyData()
    );
  }

  return getMonthlyData(trips)[periodKey] ?? emptyMonthlyData();
};

const getPriorPeriodKey = (
  mode: InsightPeriodMode,
  periodKey: string,
  monthlyData: MonthlyDataByKey
): string => {
  if (mode === "all") return "";
  if (!periodKey) return "";
  if (mode === "year") return String(Number(periodKey) - 1);
  const months = getAvailableMonths(monthlyData);
  const currentIndex = months.indexOf(periodKey);
  return currentIndex >= 0 ? months[currentIndex + 1] ?? "" : "";
};

export const getPriorInsightData = (
  trips: Trip[],
  mode: InsightPeriodMode,
  periodKey: string
): MonthlyData => {
  const monthlyData = getMonthlyData(trips);
  const priorKey = getPriorPeriodKey(mode, periodKey, monthlyData);
  return priorKey ? getInsightData(trips, mode, priorKey) : emptyMonthlyData();
};

const deltaFor = (current: number, previous: number): MetricDelta => ({
  value: current - previous,
  pct: previous > 0 ? (current - previous) / previous : 0,
});

export const getInsightDeltas = (
  current: MonthlyData,
  previous: MonthlyData
): InsightDeltas => ({
  totalHire: deltaFor(current.totalHire, previous.totalHire),
  totalExp: deltaFor(current.totalExp, previous.totalExp),
  totalProfit: deltaFor(current.totalProfit, previous.totalProfit),
  totalOutstanding: deltaFor(current.totalOutstanding, previous.totalOutstanding),
});

const tripMatchesInsightPeriod = (
  trip: Trip,
  mode: InsightPeriodMode,
  periodKey: string
): boolean => {
  if (!isReportableTrip(trip)) return false;
  if (mode === "all") return true;
  if (!periodKey) return false;
  if (mode === "year") return trip.date.startsWith(periodKey);
  return (trip.monthKey || trip.date.slice(0, 7)) === periodKey;
};

export const getTripsForInsightPeriod = (
  trips: Trip[],
  mode: InsightPeriodMode,
  periodKey: string
): Trip[] => trips.filter((trip) => tripMatchesInsightPeriod(trip, mode, periodKey));

export const getInsightQualitySignals = (
  trips: Trip[],
  mode: InsightPeriodMode,
  periodKey: string
): InsightQualitySignals => {
  const periodTrips = getTripsForInsightPeriod(trips, mode, periodKey);
  const partyCounts = new Map<string, number>();
  let lowMarginTrips = 0;
  let settledDaysTotal = 0;
  let settledCount = 0;

  periodTrips.forEach((trip) => {
    if (trip.partyName) partyCounts.set(trip.partyName, (partyCounts.get(trip.partyName) ?? 0) + 1);
    if (trip.hireAmount > 0 && calculateFinancials(trip).marginPct < 0.12) {
      lowMarginTrips += 1;
    }
    if (trip.settledAt) {
      settledDaysTotal += daysSince(trip.date, new Date(trip.settledAt));
      settledCount += 1;
    }
  });

  const repeatTripCount = periodTrips.filter(
    (trip) => trip.partyName && (partyCounts.get(trip.partyName) ?? 0) > 1
  ).length;

  return {
    repeatPartyRate: periodTrips.length > 0 ? repeatTripCount / periodTrips.length : 0,
    lowMarginShare: periodTrips.length > 0 ? lowMarginTrips / periodTrips.length : 0,
    averageDaysToSettle: settledCount > 0 ? settledDaysTotal / settledCount : 0,
  };
};

export const getRouteCostVariance = (
  trips: Trip[],
  mode: InsightPeriodMode,
  periodKey: string
): RouteCostVariance[] => {
  const periodRoutes = getInsightData(trips, mode, periodKey).routes;
  const historicalRoutes = getInsightData(trips, "all", "all").routes;

  return Object.entries(periodRoutes)
    .map(([route, stats]) => {
      const historical = historicalRoutes[route];
      const historicalAvgExpense = historical ? historical.expense / historical.trips : 0;
      const periodAvgExpense = stats.trips > 0 ? stats.expense / stats.trips : 0;
      return {
        route,
        trips: stats.trips,
        periodAvgExpense,
        historicalAvgExpense,
        variancePct:
          historicalAvgExpense > 0
            ? (periodAvgExpense - historicalAvgExpense) / historicalAvgExpense
            : 0,
      };
    })
    .filter((route) => route.trips >= 3 && route.historicalAvgExpense > 0)
    .sort((a, b) => Math.abs(b.variancePct) - Math.abs(a.variancePct));
};

export const getAgeingBucket = (ageDays: number): AgeingBucket => {
  if (ageDays <= 15) return "0-15";
  if (ageDays <= 30) return "16-30";
  if (ageDays <= 60) return "31-60";
  return "60+";
};

export const getPartyOutstanding = (trips: Trip[]): PartyOutstanding[] => {
  const byParty = new Map<string, PartyOutstanding>();

  trips.forEach((trip) => {
    const financials = calculateFinancials(trip);
    if (financials.outstanding <= 0 || trip.status === "Cancelled") return;

    const current =
      byParty.get(trip.partyName) ??
      ({
        partyName: trip.partyName,
        trips: 0,
        hire: 0,
        received: 0,
        outstanding: 0,
        oldestOpenDate: trip.date,
        ageDays: 0,
        ageingBucket: "0-15",
      } satisfies PartyOutstanding);

    current.trips += 1;
    current.hire += trip.hireAmount;
    current.received += financials.amountReceived;
    current.outstanding += financials.outstanding;
    if (trip.date < current.oldestOpenDate) current.oldestOpenDate = trip.date;
    byParty.set(trip.partyName, current);
  });

  return Array.from(byParty.values())
    .map((party) => {
      const ageDays = daysSince(party.oldestOpenDate);
      return {
        ...party,
        ageDays,
        ageingBucket: getAgeingBucket(ageDays),
      };
    })
    .sort((a, b) => b.outstanding - a.outstanding);
};

export const filterAndSortCashTrips = (
  trips: Trip[],
  filters: CashFilters
): Trip[] => {
  const query = filters.query.trim().toLowerCase();
  const filtered = trips.filter((trip) => {
    const outstanding = calculateFinancials(trip).outstanding;
    if (outstanding <= 0 || trip.status === "Cancelled") return false;
    if (filters.status !== "all" && trip.status !== filters.status) return false;
    if (filters.party !== "all" && trip.partyName !== filters.party) return false;
    if (
      filters.ageingBucket !== "all" &&
      getAgeingBucket(daysSince(trip.date)) !== filters.ageingBucket
    ) {
      return false;
    }

    if (!query) return true;
    const searchable = [
      trip.tripNo,
      trip.partyName,
      trip.vehicleNo,
      trip.origin,
      trip.destination,
      trip.status,
    ]
      .join(" ")
      .toLowerCase();
    return searchable.includes(query);
  });

  return [...filtered].sort((a, b) => {
    if (filters.sort === "largest") {
      return (
        calculateFinancials(b).outstanding -
        calculateFinancials(a).outstanding
      );
    }
    if (filters.sort === "party") {
      return a.partyName.localeCompare(b.partyName) || a.date.localeCompare(b.date);
    }
    return a.date.localeCompare(b.date);
  });
};

export const filterAndSortParties = (
  parties: PartyOutstanding[],
  filters: PartyFilters
): PartyOutstanding[] => {
  const query = filters.query.trim().toLowerCase();
  const filtered = parties.filter((party) => {
    if (
      filters.ageingBucket !== "all" &&
      party.ageingBucket !== filters.ageingBucket
    ) {
      return false;
    }
    return !query || party.partyName.toLowerCase().includes(query);
  });

  return [...filtered].sort((a, b) => {
    if (filters.sort === "oldest") return b.ageDays - a.ageDays;
    if (filters.sort === "trips") return b.trips - a.trips;
    if (filters.sort === "name") return a.partyName.localeCompare(b.partyName);
    return b.outstanding - a.outstanding;
  });
};

export const getAvailableMonths = (monthlyData: MonthlyDataByKey): string[] =>
  Object.keys(monthlyData).sort().reverse();

export const getVehiclePerformance = (monthlyData?: MonthlyData): Array<
  [string, MonthlyVehicleStats]
> =>
  Object.entries(monthlyData?.vehicles ?? {}).sort(
    ([, a], [, b]) => b.profit - a.profit
  );

export const getContributionRanking = (
  data: Record<string, ContributionStats>
): Array<[string, ContributionStats]> =>
  Object.entries(data).sort(([, a], [, b]) => b.profit - a.profit);

export const getSourceSummary = (trips: Trip[]): SourceSummary => {
  const allTime = getInsightData(trips, "all", "all");
  const topBroker = getContributionRanking(allTime.brokers)[0];
  const marketVehicleProfit = trips
    .filter((trip) => trip.vehicleSource === "Market Vehicle" && isReportableTrip(trip))
    .reduce((sum, trip) => sum + calculateFinancials(trip).operatingProfit, 0);

  return {
    topBrokerName: topBroker?.[0] ?? "",
    topBrokerProfit: topBroker?.[1].profit ?? 0,
    marketVehicleProfit,
  };
};

const mergeContributionBuckets = (
  target: Record<string, ContributionStats>,
  source: Record<string, ContributionStats>
) => {
  Object.entries(source).forEach(([key, stats]) => {
    if (!target[key]) target[key] = emptyVehicleStats();
    const current = target[key];
    current.trips += stats.trips;
    current.hire += stats.hire;
    current.expense += stats.expense;
    current.profit += stats.profit;
    current.outstanding += stats.outstanding;
    current.marginPct = current.hire > 0 ? current.profit / current.hire : 0;
  });
};

const mergeMonthlyData = (target: MonthlyData, source: MonthlyData): MonthlyData => {
  target.totalHire += source.totalHire;
  target.totalExp += source.totalExp;
  target.totalProfit += source.totalProfit;
  target.totalOutstanding += source.totalOutstanding;
  mergeContributionBuckets(target.vehicles, source.vehicles);
  mergeContributionBuckets(target.parties, source.parties);
  mergeContributionBuckets(target.routes, source.routes);
  mergeContributionBuckets(target.brokers, source.brokers);
  return target;
};
